import { Box, Image, Typography } from "@avenue-8/sidekick-ui";
import {
  HeaderSectionView,
  PresentationView,
} from "@cma/presentation-generation-logic";
import { HeaderBrandingSectionView } from "@cma/presentation-generation-logic/sections/header-branding/header-branding-section-view";
import { ImagesCarousel } from "./components/ImagesCarousel";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import { useCallback, useMemo } from "react";
import {
  PreparedForContainer,
  CoverPhotoContainerView,
} from "./HeroHeader.styles";
import { textFormatter } from "@shared/utils/text-formatter";
import { useImageGalleryStore } from "@cma/store/image-gallery.store";
import { PresentationNavbar } from "../../shared/PresentationNavbar";

type Props = PresentationSectionViewProps<
  HeaderSectionView | HeaderBrandingSectionView
> & {
  agent: PresentationView["agent"];
};

const DAYS_ON_THE_MARKET_ENABLED = false;

export const HeroHeaderView = (props: Props) => {
  const { sectionView, agent } = props;
  const {
    id,
    type,
    clientName,
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    photos,
    minSellPrice,
    maxSellPrice,
    daysOnMarket,
  } = sectionView;

  const setGalleryImages = useImageGalleryStore(
    (store) => store.setGalleryImages,
  );
  const galleryPhotos = useMemo(
    () =>
      photos?.map((photo, i) => ({
        id: `id-${i}`,
        url: photo.url,
        alt: photo.caption || `Property photo ${i}`,
      })) || [],
    [photos],
  );

  const handleClickImage = useCallback(
    (index: number) =>
      setGalleryImages({ images: galleryPhotos, initialImageIndex: index }),
    [galleryPhotos, setGalleryImages],
  );

  const officeLogo = type === "header-branding" ? sectionView.officeLogo : null;

  return (
    <Box id={id} pb={3} px={3} textAlign="center">
      <PresentationNavbar agent={agent} officeLogo={officeLogo} />

      <Box maxWidth="lg" sx={{ margin: "auto" }}>
        <PreparedForContainer>
          <Typography>Prepared for</Typography>
          <Typography fontWeight={600}>{clientName}</Typography>
        </PreparedForContainer>

        <Typography variant="h3" m={3}>
          {textFormatter.formatFullAddress({
            addressLine1,
            addressLine2,
            city,
            state,
            zipCode,
          })}
        </Typography>

        {photos && photos.length > 0 && (
          <CoverPhotoContainerView>
            <Image src={photos[0].url} alt="Cover Photo" />
          </CoverPhotoContainerView>
        )}

        {!!minSellPrice && !!maxSellPrice && (
          <Box
            mt={3}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Typography mb={1} variant="h6" fontWeight={300}>
              In today’s market, this home should sell for between
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Typography variant="h5">
                {textFormatter.currency(minSellPrice)}
              </Typography>
              <Typography variant="h6" fontWeight={300}>
                and
              </Typography>
              <Typography variant="h5">
                {textFormatter.currency(maxSellPrice)}
              </Typography>
            </Box>
          </Box>
        )}

        {DAYS_ON_THE_MARKET_ENABLED && daysOnMarket > 0 && (
          <Box
            mt={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Typography variant={"overline"}>
              {daysOnMarket} days on the market
            </Typography>
          </Box>
        )}

        <ImagesCarousel photos={photos} onClickImage={handleClickImage} />
      </Box>
    </Box>
  );
};
